'use client';

import { Icon } from '@iconify/react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  cn,
  Link,
  Tooltip,
} from '@nextui-org/react';
import { useClipboard } from '@nextui-org/use-clipboard';
import React from 'react';
import logo from '../../assets/logo.png';

export type MessageCardProps = React.HTMLAttributes<HTMLDivElement> & {
  avatar?: string;
  showFeedback?: boolean;
  message: React.ReactNode;
  isUserMessage?: boolean;
  footerMessage?: React.ReactNode;
  currentAttempt?: number;
  status?: 'success' | 'failed';
  attempts?: number;
  messageClassName?: string;
  onAttemptChange?: (attempt: number) => void;
  onMessageCopy?: (content: string | string[]) => void;
  onFeedback?: (feedback: 'like' | 'dislike') => void;
  onAttemptFeedback?: (feedback: 'like' | 'dislike' | 'same') => void;
  onSaveToVariable?: () => void;
};

const MessageCard = React.forwardRef<HTMLDivElement, MessageCardProps>(
  (
    {
      avatar,
      message,
      showFeedback,
      attempts = 1,
      currentAttempt = 1,
      status,
      onMessageCopy,
      onAttemptChange,
      onFeedback,
      onAttemptFeedback,
      className,
      messageClassName,
      footerMessage,
      isUserMessage,
      onSaveToVariable,
      ...props
    },
    ref
  ) => {
    const [feedback, setFeedback] = React.useState<'like' | 'dislike'>();
    const [attemptFeedback, setAttemptFeedback] = React.useState<
      'like' | 'dislike' | 'same'
    >();

    const messageRef = React.useRef<HTMLDivElement>(null);

    const { copied, copy } = useClipboard();

    const failedMessageClassName =
      status === 'failed'
        ? 'bg-danger-100/50 border border-danger-100 text-foreground'
        : '';
    const failedMessage = (
      <p>
        Something went wrong, if the issue persists please contact us through
        our help center at&nbsp;
        <Link href="mailto:support@acmeai.com" size="sm">
          support@acmeai.com
        </Link>
      </p>
    );

    const hasFailed = status === 'failed';

    const handleCopy = React.useCallback(() => {
      let stringValue = '';

      if (typeof message === 'string') {
        stringValue = message;
      } else if (Array.isArray(message)) {
        message.forEach((child) => {
          // "@ts-expect-error"
          const childString =
            typeof child === 'string'
              ? child
              : child?.props?.children?.toString();

          if (childString) {
            stringValue += childString + '\n';
          }
        });
      }

      const valueToCopy = stringValue || messageRef.current?.textContent || '';

      copy(valueToCopy);

      onMessageCopy?.(valueToCopy);
    }, [copy, message, onMessageCopy]);

    const handleFeedback = React.useCallback(
      (liked: boolean) => {
        setFeedback(liked ? 'like' : 'dislike');

        onFeedback?.(liked ? 'like' : 'dislike');
      },
      [onFeedback]
    );

    const handleAttemptFeedback = React.useCallback(
      (feedback: 'like' | 'dislike' | 'same') => {
        setAttemptFeedback(feedback);

        onAttemptFeedback?.(feedback);
      },
      [onAttemptFeedback]
    );

    // Check if this is a LiFT/agent avatar
    const isAgentAvatar = avatar === 'Lift' || avatar === 'LiFT';

    return (
      <div {...props} ref={ref} className={cn('flex gap-3', className)}>
        {avatar && (
          <div
            className={cn(
              'relative flex-none',
              // Hide "Me" avatar on mobile for user messages
              isUserMessage && 'hidden md:block'
            )}
          >
            <Badge
              isOneChar
              color="danger"
              content={
                <Icon
                  className="text-background"
                  icon="gravity-ui:circle-exclamation-fill"
                />
              }
              isInvisible={!hasFailed}
              placement="bottom-right"
              shape="circle"
            >
              {isAgentAvatar ? (
                // LiFT logo for agent messages
                // <div className="w-10 h-10 rounded-full object- bg-white flex items-center justify-center shadow-sm border border-gray-100">
                //   <img src={logo} alt="LiFT" className="w-6 h-6" />
                // </div>
                <div className="bg-white w-10 h-10 border rounded-full border-divider flex items-center justify-center overflow-hidden shrink-0">
                  <img
                    src={logo}
                    alt="LiFT"
                    className="object-contain object-center p-1 w-full h-full -translate-x-[3px] -translate-y-[1px]"
                  />
                </div>
              ) : isUserMessage ? (
                // "Me" badge for user messages
                <div className="w-10 h-10 rounded-full bg-[#b04d81] flex items-center justify-center text-white text-lg font-medium flex-shrink-0">
                  Me
                </div>
              ) : (
                <Avatar name={avatar} />
              )}
            </Badge>
          </div>
        )}
        <div className="flex max-w-[85%] md:max-w-[80%] flex-col gap-2 group">
          <div className="flex gap-2">
            <Card
              shadow="none"
              className={cn(
                'relative w-full px-4 py-3 border-none',
                failedMessageClassName,
                messageClassName
              )}
            >
              <div ref={messageRef} className={'text-small'}>
                {hasFailed ? failedMessage : message}
              </div>
              {showFeedback && !hasFailed && (
                <div className="absolute right-2 top-2 flex rounded-full bg-content2 shadow-small">
                  <Button
                    isIconOnly
                    radius="full"
                    size="sm"
                    variant="light"
                    onPress={handleCopy}
                  >
                    {copied ? (
                      <Icon
                        className="text-lg text-default-600"
                        icon="gravity-ui:check"
                      />
                    ) : (
                      <Icon
                        className="text-lg text-default-600"
                        icon="gravity-ui:copy"
                      />
                    )}
                  </Button>
                  <Button
                    isIconOnly
                    radius="full"
                    size="sm"
                    variant="light"
                    onPress={() => handleFeedback(true)}
                  >
                    {feedback === 'like' ? (
                      <Icon
                        className="text-lg text-default-600"
                        icon="gravity-ui:thumbs-up-fill"
                      />
                    ) : (
                      <Icon
                        className="text-lg text-default-600"
                        icon="gravity-ui:thumbs-up"
                      />
                    )}
                  </Button>
                  <Button
                    isIconOnly
                    radius="full"
                    size="sm"
                    variant="light"
                    onPress={() => handleFeedback(false)}
                  >
                    {feedback === 'dislike' ? (
                      <Icon
                        className="text-lg text-default-600"
                        icon="gravity-ui:thumbs-down-fill"
                      />
                    ) : (
                      <Icon
                        className="text-lg text-default-600"
                        icon="gravity-ui:thumbs-down"
                      />
                    )}
                  </Button>
                </div>
              )}
              {attempts > 1 && !hasFailed && (
                <div className="flex w-full items-center justify-end">
                  <button
                    onClick={() =>
                      onAttemptChange?.(
                        currentAttempt > 1 ? currentAttempt - 1 : 1
                      )
                    }
                  >
                    <Icon
                      className="cursor-pointer text-default-400 hover:text-default-500"
                      icon="gravity-ui:circle-arrow-left"
                    />
                  </button>
                  <button
                    onClick={() =>
                      onAttemptChange?.(
                        currentAttempt < attempts
                          ? currentAttempt + 1
                          : attempts
                      )
                    }
                  >
                    <Icon
                      className="cursor-pointer text-default-400 hover:text-default-500"
                      icon="gravity-ui:circle-arrow-right"
                    />
                  </button>
                  <p className="px-1 text-tiny font-medium text-default-500">
                    {currentAttempt}/{attempts}
                  </p>
                </div>
              )}
            </Card>
            {!hasFailed && !isUserMessage && onSaveToVariable && (
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Tooltip content="Save to User Profile">
                  <Button
                    isIconOnly
                    radius="full"
                    size="sm"
                    variant="shadow"
                    onPress={onSaveToVariable}
                  >
                    <Icon
                      className="text-sm text-black"
                      icon="lucide:forward"
                    />
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>

          {!showFeedback && attempts > 1 && (
            <Card className="flex items-center justify-between px-4 py-3">
              <p className="text-small text-default-600">
                Was this response better or worse?
              </p>
              <div className="flex gap-1">
                <Tooltip content="Better">
                  <Button
                    isIconOnly
                    radius="full"
                    size="sm"
                    variant="light"
                    onPress={() => handleAttemptFeedback('like')}
                  >
                    {attemptFeedback === 'like' ? (
                      <Icon
                        className="text-lg text-primary"
                        icon="gravity-ui:thumbs-up-fill"
                      />
                    ) : (
                      <Icon
                        className="text-lg text-default-600"
                        icon="gravity-ui:thumbs-up"
                      />
                    )}
                  </Button>
                </Tooltip>
                <Tooltip content="Worse">
                  <Button
                    isIconOnly
                    radius="full"
                    size="sm"
                    variant="light"
                    onPress={() => handleAttemptFeedback('dislike')}
                  >
                    {attemptFeedback === 'dislike' ? (
                      <Icon
                        className="text-lg text-default-600"
                        icon="gravity-ui:thumbs-down-fill"
                      />
                    ) : (
                      <Icon
                        className="text-lg text-default-600"
                        icon="gravity-ui:thumbs-down"
                      />
                    )}
                  </Button>
                </Tooltip>
                <Tooltip content="Same">
                  <Button
                    isIconOnly
                    radius="full"
                    size="sm"
                    variant="light"
                    onPress={() => handleAttemptFeedback('same')}
                  >
                    {attemptFeedback === 'same' ? (
                      <Icon
                        className="text-lg text-danger"
                        icon="gravity-ui:face-sad"
                      />
                    ) : (
                      <Icon
                        className="text-lg text-default-600"
                        icon="gravity-ui:face-sad"
                      />
                    )}
                  </Button>
                </Tooltip>
              </div>
            </Card>
          )}
        </div>
      </div>
    );
  }
);

export default MessageCard;

MessageCard.displayName = 'MessageCard';
